import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon, Star, Heart, Briefcase, Activity, Sparkles, Calendar as CalendarIcon, Hash, Palette, Smile, ChevronLeft, ChevronRight, Info, AlertCircle, CheckCircle2, XCircle, List, ArrowRight } from 'lucide-react';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
interface AstroDailyPageProps {
  onNavigate: (page: string) => void;
  initialTab?: 'horoscope' | 'moon' | 'calendar';
}
// --- HOROSCOPE DATA ---
const ZODIAC_SIGNS = [{
  id: 'aries',
  name: 'Овен',
  symbol: '♈',
  date: '21 мар - 19 апр',
  color: 'rose'
}, {
  id: 'taurus',
  name: 'Телец',
  symbol: '♉',
  date: '20 апр - 20 мая',
  color: 'emerald'
}, {
  id: 'gemini',
  name: 'Близнецы',
  symbol: '♊',
  date: '21 мая - 20 июн',
  color: 'amber'
}, {
  id: 'cancer',
  name: 'Рак',
  symbol: '♋',
  date: '21 июн - 22 июл',
  color: 'slate'
}, {
  id: 'leo',
  name: 'Лев',
  symbol: '♌',
  date: '23 июл - 22 авг',
  color: 'orange'
}, {
  id: 'virgo',
  name: 'Дева',
  symbol: '♍',
  date: '23 авг - 22 сен',
  color: 'teal'
}, {
  id: 'libra',
  name: 'Весы',
  symbol: '♎',
  date: '23 сен - 22 окт',
  color: 'pink'
}, {
  id: 'scorpio',
  name: 'Скорпион',
  symbol: '♏',
  date: '23 окт - 21 ноя',
  color: 'purple'
}, {
  id: 'sagittarius',
  name: 'Стрелец',
  symbol: '♐',
  date: '22 ноя - 21 дек',
  color: 'indigo'
}, {
  id: 'capricorn',
  name: 'Козерог',
  symbol: '♑',
  date: '22 дек - 19 янв',
  color: 'slate'
}, {
  id: 'aquarius',
  name: 'Водолей',
  symbol: '♒',
  date: '20 янв - 18 фев',
  color: 'cyan'
}, {
  id: 'pisces',
  name: 'Рыбы',
  symbol: '♓',
  date: '19 фев - 20 мар',
  color: 'sky'
}];
const SIGN_STYLES: Record<string, any> = {
  rose: {
    bg50: 'bg-rose-50',
    bg100: 'bg-rose-100',
    bg500: 'bg-rose-500',
    text500: 'text-rose-500',
    text700: 'text-rose-700',
    border500: 'border-rose-500'
  },
  emerald: {
    bg50: 'bg-emerald-50',
    bg100: 'bg-emerald-100',
    bg500: 'bg-emerald-500',
    text500: 'text-emerald-500',
    text700: 'text-emerald-700',
    border500: 'border-emerald-500'
  },
  amber: {
    bg50: 'bg-amber-50',
    bg100: 'bg-amber-100',
    bg500: 'bg-amber-500',
    text500: 'text-amber-500',
    text700: 'text-amber-700',
    border500: 'border-amber-500'
  },
  slate: {
    bg50: 'bg-slate-50',
    bg100: 'bg-slate-100',
    bg500: 'bg-slate-500',
    text500: 'text-slate-500',
    text700: 'text-slate-700',
    border500: 'border-slate-500'
  },
  orange: {
    bg50: 'bg-orange-50',
    bg100: 'bg-orange-100',
    bg500: 'bg-orange-500',
    text500: 'text-orange-500',
    text700: 'text-orange-700',
    border500: 'border-orange-500'
  },
  teal: {
    bg50: 'bg-teal-50',
    bg100: 'bg-teal-100',
    bg500: 'bg-teal-500',
    text500: 'text-teal-500',
    text700: 'text-teal-700',
    border500: 'border-teal-500'
  },
  pink: {
    bg50: 'bg-pink-50',
    bg100: 'bg-pink-100',
    bg500: 'bg-pink-500',
    text500: 'text-pink-500',
    text700: 'text-pink-700',
    border500: 'border-pink-500'
  },
  purple: {
    bg50: 'bg-purple-50',
    bg100: 'bg-purple-100',
    bg500: 'bg-purple-500',
    text500: 'text-purple-500',
    text700: 'text-purple-700',
    border500: 'border-purple-500'
  },
  indigo: {
    bg50: 'bg-indigo-50',
    bg100: 'bg-indigo-100',
    bg500: 'bg-indigo-500',
    text500: 'text-indigo-500',
    text700: 'text-indigo-700',
    border500: 'border-indigo-500'
  },
  cyan: {
    bg50: 'bg-cyan-50',
    bg100: 'bg-cyan-100',
    bg500: 'bg-cyan-500',
    text500: 'text-cyan-500',
    text700: 'text-cyan-700',
    border500: 'border-cyan-500'
  },
  sky: {
    bg50: 'bg-sky-50',
    bg100: 'bg-sky-100',
    bg500: 'bg-sky-500',
    text500: 'text-sky-500',
    text700: 'text-sky-700',
    border500: 'border-sky-500'
  }
};
const PERIODS = [{
  id: 'today',
  label: 'Сегодня'
}, {
  id: 'tomorrow',
  label: 'Завтра'
}, {
  id: 'week',
  label: 'Неделя'
}, {
  id: 'month',
  label: 'Месяц'
}];
const generateMockData = (signId: string, periodId: string) => {
  const hash = signId.charCodeAt(0) + signId.charCodeAt(signId.length - 1) + periodId.length;
  const forecasts = ['Сегодня отличный день для новых начинаний и смелых решений. Звезды благоприятствуют тем, кто готов взять инициативу в свои руки. Ваша интуиция будет особенно острой — прислушивайтесь к внутреннему голосу при принятии важных решений. Возможны неожиданные, но приятные новости издалека.', 'Энергия этого периода направлена на внутреннюю трансформацию. Отличное время для завершения старых дел и освобождения пространства для нового. В отношениях возможны глубокие разговоры, которые выведут вас на новый уровень понимания. Финансовые вопросы потребуют внимательности.', 'Ваша социальная активность на пике! Ожидайте интересных встреч и полезных знакомств. Звезды советуют быть открытыми к новым предложениям — одно из них может оказаться судьбоносным. В сфере здоровья обратите внимание на режим сна и питания.', 'Время сфокусироваться на карьерных целях и материальном благополучии. Ваш труд будет замечен и оценен по достоинству. В личной жизни возможны небольшие недопонимания, которые легко разрешатся, если вы проявите терпение и готовность к компромиссу.'];
  const numbers = ['7, 14, 21', '3, 9, 12', '5, 18, 22', '1, 8, 19', '4, 11, 27'];
  const colors = [{
    name: 'Изумрудный',
    bg: 'bg-emerald-500'
  }, {
    name: 'Индиго',
    bg: 'bg-indigo-500'
  }, {
    name: 'Коралловый',
    bg: 'bg-rose-400'
  }, {
    name: 'Золотой',
    bg: 'bg-amber-400'
  }, {
    name: 'Лазурный',
    bg: 'bg-sky-500'
  }];
  const moods = ['Вдохновленное', 'Спокойное', 'Энергичное', 'Задумчивое', 'Решительное'];
  return {
    forecast: forecasts[hash % forecasts.length],
    loveScore: 60 + hash % 40,
    careerScore: 50 + hash * 2 % 50,
    healthScore: 70 + hash * 3 % 30,
    number: numbers[hash % numbers.length],
    color: colors[hash % colors.length],
    mood: moods[hash % moods.length]
  };
};
const RatingBar = ({
  score,
  colorClass
}: {
  score: number;
  colorClass: string;
}) => <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mt-3" data-id="element-20056">
    <motion.div initial={{
    width: 0
  }} animate={{
    width: `${score}%`
  }} transition={{
    duration: 1,
    ease: 'easeOut'
  }} className={`h-full ${colorClass} rounded-full`} data-id="element-20057" />
  </div>;
// --- CALENDAR DATA ---
const formatMonthYear = (date: Date) => {
  const months = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  return `${months[date.getMonth()]} ${date.getFullYear()}`;
};
const getDaysInMonth = (year: number, month: number) => {
  const date = new Date(year, month, 1);
  const days = [];
  let firstDay = date.getDay() - 1;
  if (firstDay === -1) firstDay = 6;
  for (let i = 0; i < firstDay; i++) days.push(null);
  while (date.getMonth() === month) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
};
const currentTransits = [{
  planet: '☉ Солнце',
  sign: '♓ Рыбы',
  degree: "10°13'",
  element: 'water'
}, {
  planet: '☽ Луна',
  sign: '♒ Водолей',
  degree: "6°40'",
  element: 'air'
}, {
  planet: '☿ Меркурий',
  sign: '♓ Рыбы',
  degree: "22°02'",
  element: 'water'
}, {
  planet: '♀ Венера',
  sign: '♈ Овен',
  degree: "22°59'",
  element: 'fire'
}, {
  planet: '♂ Марс',
  sign: '♋ Рак',
  degree: "28°36'",
  element: 'water'
}, {
  planet: '♃ Юпитер',
  sign: '♋ Рак',
  degree: "15°15'",
  element: 'water'
}, {
  planet: '♄ Сатурн',
  sign: '♈ Овен',
  degree: "1°42'",
  element: 'fire'
}];
const elementColors: Record<string, string> = {
  fire: 'text-red-600',
  earth: 'text-emerald-600',
  air: 'text-indigo-600',
  water: 'text-sky-600'
};
const allEvents = [{
  id: 10,
  dateStr: '2024-09-02',
  displayDate: '2 Сентября',
  type: 'Новолуние',
  title: 'Новолуние в Деве',
  desc: 'Время для наведения порядка, планирования и внимания к здоровью.',
  icon: <Moon className="w-5 h-5" data-id="element-20058" />,
  color: 'text-emerald-500',
  bg: 'bg-emerald-100'
}, {
  id: 11,
  dateStr: '2024-09-18',
  displayDate: '18 Сентября',
  type: 'Затмение',
  title: 'Лунное затмение в Рыбах',
  desc: 'Завершение эмоциональных циклов, раскрытие тайн и интуитивных прозрений.',
  icon: <Moon className="w-5 h-5" data-id="element-20059" />,
  color: 'text-indigo-500',
  bg: 'bg-indigo-100'
}, {
  id: 1,
  dateStr: '2024-10-02',
  displayDate: '2 Октября',
  type: 'Затмение',
  title: 'Солнечное затмение в Весах',
  desc: 'Время кармических изменений в сфере партнерства и отношений.',
  icon: <Sun className="w-5 h-5" data-id="element-20060" />,
  color: 'text-amber-500',
  bg: 'bg-amber-100'
}, {
  id: 4,
  dateStr: '2024-10-09',
  displayDate: '9 Октября',
  type: 'Ретроград',
  title: 'Юпитер становится ретроградным',
  desc: 'Переоценка социальных связей, образования и мировоззрения.',
  icon: <AlertCircle className="w-5 h-5" data-id="element-20061" />,
  color: 'text-rose-500',
  bg: 'bg-rose-100'
}, {
  id: 5,
  dateStr: '2024-10-17',
  displayDate: '17 Октября',
  type: 'Полнолуние',
  title: 'Суперлуние в Овне',
  desc: 'Мощный всплеск энергии, кульминация личных проектов и инициатив.',
  icon: <Moon className="w-5 h-5" data-id="element-20062" />,
  color: 'text-rose-500',
  bg: 'bg-rose-100'
}, {
  id: 6,
  dateStr: '2024-11-01',
  displayDate: '1 Ноября',
  type: 'Новолуние',
  title: 'Новолуние в Скорпионе',
  desc: 'Глубокие трансформации, работа с подсознанием и финансами.',
  icon: <Moon className="w-5 h-5" data-id="element-20063" />,
  color: 'text-purple-500',
  bg: 'bg-purple-100'
}, {
  id: 7,
  dateStr: '2024-11-15',
  displayDate: '15 Ноября',
  type: 'Ингрессия',
  title: 'Сатурн выходит из ретрограда',
  desc: 'Ускорение в делах, связанных с карьерой, структурой и ответственностью.',
  icon: <Star className="w-5 h-5" data-id="element-20064" />,
  color: 'text-emerald-500',
  bg: 'bg-emerald-100'
}, {
  id: 8,
  dateStr: '2024-11-26',
  displayDate: '26 Ноября',
  type: 'Ретроград',
  title: 'Ретроградный Меркурий в Стрельце',
  desc: 'Задержки в путешествиях, обучении и юридических вопросах.',
  icon: <AlertCircle className="w-5 h-5" data-id="element-20065" />,
  color: 'text-rose-500',
  bg: 'bg-rose-100'
}, {
  id: 9,
  dateStr: '2024-12-06',
  displayDate: '6 Декабря',
  type: 'Ретроград',
  title: 'Ретроградный Марс во Льве',
  desc: 'Снижение энергии, пересмотр творческих проектов и способов самовыражения.',
  icon: <AlertCircle className="w-5 h-5" data-id="element-20066" />,
  color: 'text-rose-500',
  bg: 'bg-rose-100'
}, {
  id: 3,
  dateStr: '2024-12-15',
  displayDate: '15 Декабря',
  type: 'Ингрессия',
  title: 'Меркурий выходит из ретрограда',
  desc: 'Улучшение коммуникаций, зеленая улица для новых договоренностей.',
  icon: <Star className="w-5 h-5" data-id="element-20067" />,
  color: 'text-emerald-500',
  bg: 'bg-emerald-100'
}, {
  id: 12,
  dateStr: '2024-12-30',
  displayDate: '30 Декабря',
  type: 'Новолуние',
  title: 'Новолуние в Козероге',
  desc: 'Идеальное время для постановки долгосрочных целей на новый год.',
  icon: <Moon className="w-5 h-5" data-id="element-20068" />,
  color: 'text-slate-500',
  bg: 'bg-slate-100'
}];
export function AstroDailyPage({
  onNavigate,
  initialTab = 'horoscope'
}: AstroDailyPageProps) {
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => {
    setActiveTab(initialTab);
    window.scrollTo(0, 0);
  }, [initialTab]);
  // Horoscope State
  const [activeSign, setActiveSign] = useState(ZODIAC_SIGNS[0].id);
  const [activePeriod, setActivePeriod] = useState('today');
  const zodiacScrollRef = useRef<HTMLDivElement>(null);
  const selectedSign = ZODIAC_SIGNS.find(s => s.id === activeSign) || ZODIAC_SIGNS[0];
  const signStyles = SIGN_STYLES[selectedSign.color] || SIGN_STYLES.slate;
  const horoscopeData = useMemo(() => generateMockData(activeSign, activePeriod), [activeSign, activePeriod]);
  const scrollZodiac = (direction: 'left' | 'right') => {
    if (zodiacScrollRef.current) {
      const scrollAmount = 200;
      zodiacScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  // Moon State
  const currentMoonDateStr = new Date().toLocaleDateString('ru-RU', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  // Calendar State
  const [calendarView, setCalendarView] = useState<'month' | 'list'>('list');
  const [currentDate, setCurrentDate] = useState(new Date(2024, 9, 1)); // October 2024
  const [activeTooltipDay, setActiveTooltipDay] = useState<number | null>(null);
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const currentMonthEvents = useMemo(() => {
    return allEvents.filter(event => {
      const eventDate = new Date(event.dateStr);
      return eventDate.getMonth() === currentDate.getMonth() && eventDate.getFullYear() === currentDate.getFullYear();
    }).sort((a, b) => new Date(a.dateStr).getTime() - new Date(b.dateStr).getTime());
  }, [currentDate]);
  const calendarDays = useMemo(() => getDaysInMonth(currentDate.getFullYear(), currentDate.getMonth()), [currentDate]);
  const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
  return <div className="min-h-screen bg-slate-50 pb-16 sm:pb-24" data-id="element-20069">
      {/* Shared Header */}
      <div className="bg-white border-b border-slate-200 pt-6 pb-0" data-id="element-20070">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-20071">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Астро-календарь'
        }]} className="mb-6" data-id="element-20072" />

          <div className="text-center max-w-2xl mx-auto mb-8" data-id="element-20073">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3" data-id="element-20074">
              Астро-календарь
            </h1>
            <p className="text-base sm:text-lg text-slate-600 px-4 sm:px-0" data-id="element-20075">
              Ваш ежедневный путеводитель по звёздным ритмам, лунным циклам и
              важным астрологическим событиям.
            </p>
          </div>

          {/* Custom Inline Tabs */}
          <div className="flex justify-center mb-6" data-id="element-20076">
            <div className="bg-slate-100 p-1 rounded-xl flex w-full sm:w-auto" data-id="element-20077">
              <button onClick={() => setActiveTab('horoscope')} className={`flex items-center justify-center flex-1 sm:flex-none px-3 sm:px-6 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'horoscope' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}`} data-id="element-20078">
                <Sun className="w-4 h-4 mr-1.5 sm:mr-2 text-amber-500" data-id="element-20079" />{' '}
                Гороскоп
              </button>
              <button onClick={() => setActiveTab('moon')} className={`flex items-center justify-center flex-1 sm:flex-none px-3 sm:px-6 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'moon' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}`} data-id="element-20080">
                <Moon className="w-4 h-4 mr-1.5 sm:mr-2 text-indigo-500" data-id="element-20081" /> Луна
              </button>
              <button onClick={() => setActiveTab('calendar')} className={`flex items-center justify-center flex-1 sm:flex-none px-3 sm:px-6 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'calendar' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}`} data-id="element-20082">
                <CalendarIcon className="w-4 h-4 mr-1.5 sm:mr-2 text-emerald-500" data-id="element-20083" />{' '}
                Календарь
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-id="element-20084">
        {/* ==================== HOROSCOPE TAB ==================== */}
        {activeTab === 'horoscope' && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-20085">
            {/* Zodiac Selector */}
            <div className="relative -mx-4 sm:mx-0 mb-8" data-id="element-20086">
              <button onClick={() => scrollZodiac('left')} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/90 shadow-md border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-white hover:text-slate-900 transition-all lg:hidden" data-id="element-20087">
                <ChevronLeft className="w-4 h-4" data-id="element-20088" />
              </button>

              <div ref={zodiacScrollRef} className="flex overflow-x-auto hide-scrollbar gap-1 sm:gap-1.5 pb-2 px-8 sm:px-0 lg:px-0 snap-x scroll-smooth" data-id="element-20089">
                {ZODIAC_SIGNS.map(sign => {
              const sStyle = SIGN_STYLES[sign.color] || SIGN_STYLES.slate;
              const isActive = activeSign === sign.id;
              return <button key={sign.id} onClick={() => setActiveSign(sign.id)} className={`flex flex-col items-center min-w-[64px] sm:min-w-[72px] lg:min-w-0 lg:flex-1 py-2 px-2 sm:px-3 rounded-xl transition-all snap-center shrink-0 ${isActive ? `${sStyle.bg50} border ${sStyle.border500} shadow-sm` : 'bg-white border border-transparent hover:bg-slate-50 hover:border-slate-200'}`} data-id="element-20090">
                      <span className="text-xl sm:text-2xl mb-0.5 sm:mb-1" data-id="element-20091">
                        {sign.symbol}
                      </span>
                      <span className={`text-[11px] sm:text-xs font-bold leading-tight ${isActive ? sStyle.text700 : 'text-slate-700'}`} data-id="element-20092">
                        {sign.name}
                      </span>
                      <span className="text-[8px] sm:text-[9px] text-slate-400 mt-0.5 whitespace-nowrap" data-id="element-20093">
                        {sign.date}
                      </span>
                    </button>;
            })}
              </div>

              <button onClick={() => scrollZodiac('right')} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/90 shadow-md border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-white hover:text-slate-900 transition-all lg:hidden" data-id="element-20094">
                <ChevronRight className="w-4 h-4" data-id="element-20095" />
              </button>
              <div className="absolute top-0 left-8 w-4 h-full bg-gradient-to-r from-white to-transparent pointer-events-none lg:hidden" data-id="element-20096" />
              <div className="absolute top-0 right-8 w-4 h-full bg-gradient-to-l from-white to-transparent pointer-events-none lg:hidden" data-id="element-20097" />
            </div>

            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 sm:mb-8 gap-4" data-id="element-20098">
              <div className="flex items-center gap-4" data-id="element-20099">
                <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full ${signStyles.bg100} flex items-center justify-center text-2xl sm:text-3xl shadow-inner shrink-0`} data-id="element-20100">
                  {selectedSign.symbol}
                </div>
                <div data-id="element-20101">
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-20102">
                    {selectedSign.name}
                  </h2>
                  <p className="text-sm sm:text-base text-slate-500" data-id="element-20103">
                    {selectedSign.date}
                  </p>
                </div>
              </div>

              <div className="bg-white p-1 rounded-xl shadow-sm border border-slate-200 flex overflow-x-auto hide-scrollbar" data-id="element-20104">
                {PERIODS.map(period => <button key={period.id} onClick={() => setActivePeriod(period.id)} className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${activePeriod === period.id ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}`} data-id="element-20105">
                    {period.label}
                  </button>)}
              </div>
            </div>

            <Card className="mb-6 sm:mb-8 overflow-hidden border-0 shadow-md" data-id="element-20106">
              <div className={`h-1.5 sm:h-2 ${signStyles.bg500} w-full`} data-id="element-20107" />
              <div className="p-5 sm:p-8" data-id="element-20108">
                <div className="flex items-center gap-2 mb-3 sm:mb-4" data-id="element-20109">
                  <Sparkles className={`w-5 h-5 ${signStyles.text500}`} data-id="element-20110" />
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900" data-id="element-20111">
                    Общий прогноз
                  </h3>
                </div>
                <p className="text-slate-700 leading-relaxed text-base sm:text-lg" data-id="element-20112">
                  {horoscopeData.forecast}
                </p>
              </div>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8" data-id="element-20113">
              <Card padding="md" className="border-rose-100 bg-rose-50/30 hover:shadow-md transition-shadow flex flex-col" data-id="element-20114">
                <div className="flex items-center justify-between mb-3" data-id="element-20115">
                  <div className="flex items-center gap-2" data-id="element-20116">
                    <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center" data-id="element-20117">
                      <Heart className="w-4 h-4 text-rose-600" data-id="element-20118" />
                    </div>
                    <h4 className="font-bold text-slate-900" data-id="element-20119">Любовь</h4>
                  </div>
                  <span className="text-sm font-bold text-rose-600" data-id="element-20120">
                    {horoscopeData.loveScore}%
                  </span>
                </div>
                <p className="text-sm text-slate-600 flex-grow" data-id="element-20121">
                  Романтические энергии на подъеме. Отличное время для
                  откровенных разговоров с партнером или новых знакомств.
                </p>
                <RatingBar score={horoscopeData.loveScore} colorClass="bg-rose-500" data-id="element-20122" />
              </Card>

              <Card padding="md" className="border-indigo-100 bg-indigo-50/30 hover:shadow-md transition-shadow flex flex-col" data-id="element-20123">
                <div className="flex items-center justify-between mb-3" data-id="element-20124">
                  <div className="flex items-center gap-2" data-id="element-20125">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center" data-id="element-20126">
                      <Briefcase className="w-4 h-4 text-indigo-600" data-id="element-20127" />
                    </div>
                    <h4 className="font-bold text-slate-900" data-id="element-20128">Карьера</h4>
                  </div>
                  <span className="text-sm font-bold text-indigo-600" data-id="element-20129">
                    {horoscopeData.careerScore}%
                  </span>
                </div>
                <p className="text-sm text-slate-600 flex-grow" data-id="element-20130">
                  Проявите лидерские качества. Ваши идеи будут услышаны
                  руководством. Возможны небольшие финансовые поступления.
                </p>
                <RatingBar score={horoscopeData.careerScore} colorClass="bg-indigo-500" data-id="element-20131" />
              </Card>

              <Card padding="md" className="border-emerald-100 bg-emerald-50/30 hover:shadow-md transition-shadow flex flex-col sm:col-span-2 lg:col-span-1" data-id="element-20132">
                <div className="flex items-center justify-between mb-3" data-id="element-20133">
                  <div className="flex items-center gap-2" data-id="element-20134">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-20135">
                      <Activity className="w-4 h-4 text-emerald-600" data-id="element-20136" />
                    </div>
                    <h4 className="font-bold text-slate-900" data-id="element-20137">Здоровье</h4>
                  </div>
                  <span className="text-sm font-bold text-emerald-600" data-id="element-20138">
                    {horoscopeData.healthScore}%
                  </span>
                </div>
                <p className="text-sm text-slate-600 flex-grow" data-id="element-20139">
                  Энергетический потенциал высок, но не забывайте об отдыхе.
                  Вечерняя прогулка поможет снять напряжение дня.
                </p>
                <RatingBar score={horoscopeData.healthScore} colorClass="bg-emerald-500" data-id="element-20140" />
              </Card>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 shadow-sm" data-id="element-20141">
              <div className="flex items-center gap-3 p-3 sm:p-0 rounded-xl sm:rounded-none bg-slate-50 sm:bg-transparent border border-slate-100 sm:border-none" data-id="element-20142">
                <div className="w-10 h-10 rounded-full bg-white sm:bg-slate-100 flex items-center justify-center shadow-sm sm:shadow-none shrink-0" data-id="element-20143">
                  <Hash className="w-5 h-5 text-slate-500" data-id="element-20144" />
                </div>
                <div data-id="element-20145">
                  <div className="text-[10px] sm:text-xs text-slate-500 font-medium uppercase tracking-wider" data-id="element-20146">
                    Число дня
                  </div>
                  <div className="text-base sm:text-lg font-bold text-slate-900" data-id="element-20147">
                    {horoscopeData.number}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 sm:p-0 rounded-xl sm:rounded-none bg-slate-50 sm:bg-transparent border border-slate-100 sm:border-none" data-id="element-20148">
                <div className="w-10 h-10 rounded-full bg-white sm:bg-slate-100 flex items-center justify-center shadow-sm sm:shadow-none shrink-0" data-id="element-20149">
                  <Palette className="w-5 h-5 text-slate-500" data-id="element-20150" />
                </div>
                <div data-id="element-20151">
                  <div className="text-[10px] sm:text-xs text-slate-500 font-medium uppercase tracking-wider" data-id="element-20152">
                    Цвет удачи
                  </div>
                  <div className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2" data-id="element-20153">
                    <span className={`w-3 h-3 rounded-full ${horoscopeData.color.bg}`} data-id="element-20154"></span>
                    {horoscopeData.color.name}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 sm:p-0 rounded-xl sm:rounded-none bg-slate-50 sm:bg-transparent border border-slate-100 sm:border-none" data-id="element-20155">
                <div className="w-10 h-10 rounded-full bg-white sm:bg-slate-100 flex items-center justify-center shadow-sm sm:shadow-none shrink-0" data-id="element-20156">
                  <Smile className="w-5 h-5 text-slate-500" data-id="element-20157" />
                </div>
                <div data-id="element-20158">
                  <div className="text-[10px] sm:text-xs text-slate-500 font-medium uppercase tracking-wider" data-id="element-20159">
                    Настроение
                  </div>
                  <div className="text-base sm:text-lg font-bold text-slate-900" data-id="element-20160">
                    {horoscopeData.mood}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>}

        {/* ==================== MOON TAB ==================== */}
        {activeTab === 'moon' && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-20161">
            <div className="bg-slate-900 text-white p-8 sm:p-10 mb-8 relative overflow-hidden rounded-2xl shadow-xl" data-id="element-20162">
              <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-20163" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-20164" />
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-20165">
                <div className="max-w-2xl" data-id="element-20166">
                  <Badge variant="pro" className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 mb-4" data-id="element-20167">
                    Обновляется ежедневно
                  </Badge>
                  <h2 className="text-3xl sm:text-4xl font-bold mb-4" data-id="element-20168">
                    Лунный календарь
                  </h2>
                  <p className="text-xl text-slate-300 mb-2 capitalize" data-id="element-20169">
                    {currentMoonDateStr}
                  </p>
                  <p className="text-slate-400" data-id="element-20170">
                    Следите за фазами Луны, чтобы планировать важные дела,
                    стрижки, посадки и понимать общий энергетический фон дня.
                  </p>
                </div>
                <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 120,
              repeat: Infinity,
              ease: 'linear'
            }} className="w-40 h-40 sm:w-48 sm:h-48 rounded-full bg-gradient-to-br from-slate-100 to-slate-400 shadow-[0_0_50px_rgba(255,255,255,0.2)] flex items-center justify-center relative overflow-hidden shrink-0" data-id="element-20171">
                  <div className="absolute inset-0 bg-slate-900 rounded-full transform translate-x-1/4 scale-110 opacity-80 mix-blend-multiply" data-id="element-20172" />
                  <div className="absolute inset-0 shadow-inner rounded-full" data-id="element-20173" />
                </motion.div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-20174">
              <div className="lg:col-span-2 space-y-8" data-id="element-20175">
                <Card className="p-6 sm:p-8 shadow-xl border-0" data-id="element-20176">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-20177">
                    Энергия сегодняшнего дня
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8" data-id="element-20178">
                    <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100" data-id="element-20179">
                      <div className="text-sm text-slate-500 font-medium mb-1" data-id="element-20180">
                        Фаза Луны
                      </div>
                      <div className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-20181">
                        Растущая Луна{' '}
                        <span className="text-indigo-500" data-id="element-20182">🌔</span>
                      </div>
                      <div className="text-sm text-slate-600 mt-2" data-id="element-20183">
                        Освещенность: 78%
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100" data-id="element-20184">
                      <div className="text-sm text-slate-500 font-medium mb-1" data-id="element-20185">
                        Лунный день
                      </div>
                      <div className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-20186">
                        11-й лунный день
                      </div>
                      <div className="text-sm text-slate-600 mt-2" data-id="element-20187">
                        Символ: Огненный меч
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100" data-id="element-20188">
                      <div className="text-sm text-slate-500 font-medium mb-1" data-id="element-20189">
                        Луна в знаке
                      </div>
                      <div className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-20190">
                        Лев <span className="text-amber-500" data-id="element-20191">♌</span>
                      </div>
                      <div className="text-sm text-slate-600 mt-2" data-id="element-20192">
                        Стихия: Огонь
                      </div>
                    </div>
                    <div className="bg-rose-50 rounded-2xl p-5 border border-rose-100" data-id="element-20193">
                      <div className="text-sm text-rose-600 font-medium mb-1 flex items-center gap-1" data-id="element-20194">
                        <AlertCircle className="w-4 h-4" data-id="element-20195" /> Внимание
                      </div>
                      <div className="text-xl font-bold text-slate-900" data-id="element-20196">
                        Луна без курса
                      </div>
                      <div className="text-sm text-slate-600 mt-2" data-id="element-20197">
                        С 14:30 до 17:45 (МСК)
                      </div>
                    </div>
                  </div>
                  <div className="prose prose-slate max-w-none" data-id="element-20198">
                    <p data-id="element-20199">
                      Один из самых энергетически мощных дней лунного месяца.
                      Энергия бьет ключом, пробуждаются скрытые резервы
                      организма. Отличный день для активных действий, начала
                      новых проектов и физических нагрузок.
                    </p>
                    <p data-id="element-20200">
                      Луна во Льве добавляет желания быть в центре внимания,
                      творить и радовать себя. Однако в период "Луны без курса"
                      (с 14:30 до 17:45) лучше воздержаться от принятия важных
                      решений и подписания документов — дела, начатые в это
                      время, могут пойти не по плану.
                    </p>
                  </div>
                </Card>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-20201">
                  <Card className="p-6 border-emerald-200 bg-emerald-50/30" data-id="element-20202">
                    <div className="flex items-center gap-2 mb-4" data-id="element-20203">
                      <CheckCircle2 className="w-6 h-6 text-emerald-500" data-id="element-20204" />
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-20205">
                        Благоприятно
                      </h3>
                    </div>
                    <ul className="space-y-3" data-id="element-20206">
                      <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-20207">
                        <span className="text-emerald-500 mt-0.5" data-id="element-20208">•</span>{' '}
                        Творческие начинания
                      </li>
                      <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-20209">
                        <span className="text-emerald-500 mt-0.5" data-id="element-20210">•</span>{' '}
                        Физическая активность и спорт
                      </li>
                      <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-20211">
                        <span className="text-emerald-500 mt-0.5" data-id="element-20212">•</span>{' '}
                        Стрижка волос (для быстрого роста)
                      </li>
                      <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-20213">
                        <span className="text-emerald-500 mt-0.5" data-id="element-20214">•</span>{' '}
                        Покупки одежды и украшений
                      </li>
                    </ul>
                  </Card>
                  <Card className="p-6 border-rose-200 bg-rose-50/30" data-id="element-20215">
                    <div className="flex items-center gap-2 mb-4" data-id="element-20216">
                      <XCircle className="w-6 h-6 text-rose-500" data-id="element-20217" />
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-20218">
                        Не рекомендуется
                      </h3>
                    </div>
                    <ul className="space-y-3" data-id="element-20219">
                      <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-20220">
                        <span className="text-rose-500 mt-0.5" data-id="element-20221">•</span>{' '}
                        Пассивность и лень
                      </li>
                      <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-20222">
                        <span className="text-rose-500 mt-0.5" data-id="element-20223">•</span>{' '}
                        Хирургические операции на сердце
                      </li>
                      <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-20224">
                        <span className="text-rose-500 mt-0.5" data-id="element-20225">•</span> Важные
                        дела в период "Луны без курса"
                      </li>
                      <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-20226">
                        <span className="text-rose-500 mt-0.5" data-id="element-20227">•</span>{' '}
                        Конфликты с начальством
                      </li>
                    </ul>
                  </Card>
                </div>
              </div>

              <div className="space-y-6" data-id="element-20228">
                <Card className="p-6" data-id="element-20229">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-20230">
                    <CalendarIcon className="w-5 h-5 text-indigo-500" data-id="element-20231" />{' '}
                    Ближайшие фазы
                  </h3>
                  <div className="space-y-4" data-id="element-20232">
                    <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-id="element-20233">
                      <div className="flex items-center gap-3" data-id="element-20234">
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-lg" data-id="element-20235">
                          🌕
                        </div>
                        <div data-id="element-20236">
                          <div className="text-sm font-bold text-slate-900" data-id="element-20237">
                            Полнолуние
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-20238">в Деве</div>
                        </div>
                      </div>
                      <div className="text-sm font-medium text-slate-700" data-id="element-20239">
                        24 фев
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-id="element-20240">
                      <div className="flex items-center gap-3" data-id="element-20241">
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-lg" data-id="element-20242">
                          🌗
                        </div>
                        <div data-id="element-20243">
                          <div className="text-sm font-bold text-slate-900" data-id="element-20244">
                            Третья четверть
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-20245">
                            в Стрельце
                          </div>
                        </div>
                      </div>
                      <div className="text-sm font-medium text-slate-700" data-id="element-20246">
                        3 мар
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-id="element-20247">
                      <div className="flex items-center gap-3" data-id="element-20248">
                        <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center text-lg" data-id="element-20249">
                          🌑
                        </div>
                        <div data-id="element-20250">
                          <div className="text-sm font-bold text-slate-900" data-id="element-20251">
                            Новолуние
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-20252">в Рыбах</div>
                        </div>
                      </div>
                      <div className="text-sm font-medium text-slate-700" data-id="element-20253">
                        10 мар
                      </div>
                    </div>
                  </div>
                </Card>
                <div className="bg-indigo-50 rounded-2xl p-6 border border-indigo-100" data-id="element-20254">
                  <div className="flex items-start gap-3" data-id="element-20255">
                    <Info className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" data-id="element-20256" />
                    <div data-id="element-20257">
                      <h4 className="font-bold text-indigo-900 mb-2" data-id="element-20258">
                        Что такое Луна без курса?
                      </h4>
                      <p className="text-sm text-indigo-800/80 leading-relaxed" data-id="element-20259">
                        Это период, когда Луна сделала свой последний мажорный
                        аспект в одном знаке зодиака, но еще не перешла в
                        следующий. В это время дела часто идут впустую, планы
                        срываются, а начатые проекты не приносят ожидаемого
                        результата.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>}

        {/* ==================== CALENDAR TAB ==================== */}
        {activeTab === 'calendar' && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-20260">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8" data-id="element-20261">
              <div className="flex items-center flex-wrap bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium border border-indigo-100" data-id="element-20262">
                <Moon className="w-4 h-4 mr-1.5 sm:mr-2 text-indigo-500" data-id="element-20263" />
                <span data-id="element-20264">Луна без курса:</span>
                <span className="ml-1.5 sm:ml-2 font-bold" data-id="element-20265">14:56 - 18:30</span>
                <div className="group relative ml-2 cursor-help" data-id="element-20266">
                  <Info className="w-4 h-4 text-indigo-400" data-id="element-20267" />
                  <div className="absolute bottom-full right-0 sm:left-1/2 sm:transform sm:-translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-20268">
                    Период неэффективности. Не рекомендуется начинать новые
                    важные дела.
                  </div>
                </div>
              </div>

              <div className="flex bg-slate-100 p-1 rounded-xl w-fit" data-id="element-20269">
                <button onClick={() => setCalendarView('month')} className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${calendarView === 'month' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-20270">
                  <CalendarIcon className="w-4 h-4 mr-2" data-id="element-20271" /> Месяц
                </button>
                <button onClick={() => setCalendarView('list')} className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${calendarView === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-20272">
                  <List className="w-4 h-4 mr-2" data-id="element-20273" /> Список
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-10" data-id="element-20274">
              <Card padding="md" className="border-amber-200 bg-amber-50/50 lg:col-span-1 self-start" data-id="element-20275">
                <div className="flex items-start gap-3" data-id="element-20276">
                  <div className="p-2 rounded-xl bg-amber-100 text-amber-600" data-id="element-20277">
                    <Moon className="w-5 h-5" data-id="element-20278" />
                  </div>
                  <div data-id="element-20279">
                    <div className="font-bold text-slate-900 mb-1" data-id="element-20280">
                      Луна без курса
                    </div>
                    <div className="text-sm text-slate-700 mb-2" data-id="element-20281">
                      ☽ Луна в Водолее • до{' '}
                      <span className="font-semibold" data-id="element-20282">14:56 МСК</span>
                    </div>
                    <div className="flex items-start gap-1.5 text-xs text-slate-500" data-id="element-20283">
                      <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-20284" />
                      <span data-id="element-20285">
                        Не рекомендуется начинать важные дела, подписывать
                        документы и делать крупные покупки.
                      </span>
                    </div>
                  </div>
                </div>
              </Card>

              <Card padding="none" className="lg:col-span-2 overflow-hidden" data-id="element-20286">
                <div className="px-4 py-3 bg-slate-50/80 border-b border-slate-100 flex items-center justify-between" data-id="element-20287">
                  <div className="font-bold text-slate-900 text-sm" data-id="element-20288">
                    Текущие транзиты
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-20289">
                    Сегодня, 22:46 МСК
                  </div>
                </div>
                <div className="divide-y divide-slate-100" data-id="element-20290">
                  {currentTransits.map((t, i) => <div key={i} className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50/50 transition-colors" data-id="element-20291">
                      <span className="text-xs sm:text-sm text-slate-500 w-24 sm:w-28 flex-shrink-0" data-id="element-20292">
                        {t.planet}
                      </span>
                      <span className={`font-bold text-xs sm:text-sm ${elementColors[t.element]} flex-1 text-center`} data-id="element-20293">
                        {t.sign}
                      </span>
                      <span className="text-[10px] sm:text-xs text-slate-400 font-mono w-14 sm:w-16 text-right" data-id="element-20294">
                        {t.degree}
                      </span>
                    </div>)}
                </div>
              </Card>
            </div>

            <div className="flex items-center justify-between mb-4 sm:mb-8" data-id="element-20295">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 capitalize" data-id="element-20296">
                {formatMonthYear(currentDate)}
              </h2>
              <div className="flex space-x-2" data-id="element-20297">
                <Button variant="outline" size="sm" icon={<ChevronLeft className="w-4 h-4" data-id="element-20299" />} onClick={prevMonth} data-id="element-20298" />
                <Button variant="outline" size="sm" icon={<ChevronRight className="w-4 h-4" data-id="element-20301" />} onClick={nextMonth} data-id="element-20300" />
              </div>
            </div>

            {calendarView === 'list' ? <div className="space-y-4" data-id="element-20302">
                {currentMonthEvents.length > 0 ? currentMonthEvents.map(event => <Card key={event.id} hoverable padding="lg" className="flex flex-col sm:flex-row gap-6 cursor-pointer" onClick={() => onNavigate('calendar-event')} data-id="element-20303">
                      <div className="flex-shrink-0 text-center sm:text-left sm:w-32" data-id="element-20304">
                        <div className="text-sm text-slate-500 font-medium" data-id="element-20305">
                          {event.displayDate.split(' ')[1]}
                        </div>
                        <div className="text-3xl font-bold text-slate-900" data-id="element-20306">
                          {event.displayDate.split(' ')[0]}
                        </div>
                      </div>
                      <div className="flex-grow" data-id="element-20307">
                        <div className="flex items-center gap-3 mb-2" data-id="element-20308">
                          <div className={`p-1.5 rounded-lg ${event.bg} ${event.color}`} data-id="element-20309">
                            {event.icon}
                          </div>
                          <Badge variant="neutral" data-id="element-20310">{event.type}</Badge>
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-20311">
                          {event.title}
                        </h3>
                        <p className="text-slate-600" data-id="element-20312">{event.desc}</p>
                      </div>
                      <div className="flex-shrink-0 flex items-center justify-center sm:justify-end" data-id="element-20313">
                        <Button variant="ghost" className="hidden sm:flex" onClick={e => {
                e.stopPropagation();
                onNavigate('calendar-event');
              }} data-id="element-20314">
                          Подробнее
                        </Button>
                      </div>
                    </Card>) : <Card padding="xl" className="text-center text-slate-500" data-id="element-20315">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" data-id="element-20316" />
                    <p className="text-lg font-medium text-slate-900 mb-2" data-id="element-20317">
                      Нет важных событий
                    </p>
                    <p data-id="element-20318">В этом месяце нет значимых астрологических событий.</p>
                  </Card>}
              </div> : <Card padding="none" className="overflow-hidden overflow-y-visible" onClick={() => setActiveTooltipDay(null)} data-id="element-20319">
                <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200" data-id="element-20320">
                  {weekDays.map((day, i) => <div key={i} className="py-2 sm:py-3 text-center text-[11px] sm:text-sm font-semibold text-slate-600" data-id="element-20321">
                      {day}
                    </div>)}
                </div>
                <div className="grid grid-cols-7 auto-rows-[56px] sm:auto-rows-[100px] md:auto-rows-[120px] divide-x divide-y divide-slate-100" data-id="element-20322">
                  {calendarDays.map((date, i) => {
              if (!date) return <div key={`empty-${i}`} className="bg-slate-50/50" data-id="element-20323"></div>;
              const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
              const dayEvents = currentMonthEvents.filter(e => e.dateStr === dateStr);
              const isToday = new Date().toDateString() === date.toDateString();
              return <div key={i} className={`group/day p-1 sm:p-2 relative transition-colors ${dayEvents.length > 0 ? 'hover:bg-slate-50 cursor-pointer' : ''} ${isToday ? 'bg-indigo-50/30' : ''}`} onClick={e => {
                if (dayEvents.length > 0) {
                  e.stopPropagation();
                  setActiveTooltipDay(activeTooltipDay === i ? null : i);
                }
              }} data-id="element-20324">
                        <div className={`text-xs sm:text-sm font-medium w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-full mb-0.5 sm:mb-1 ${isToday ? 'bg-indigo-600 text-white' : 'text-slate-700'}`} data-id="element-20325">
                          {date.getDate()}
                        </div>
                        <div className="flex gap-0.5 flex-wrap sm:hidden" data-id="element-20326">
                          {dayEvents.map(event => <div key={event.id} className={`w-1.5 h-1.5 rounded-full ${event.bg.replace('100', '400')}`} data-id="element-20327" />)}
                        </div>
                        <div className="hidden sm:block space-y-1 overflow-y-auto hide-scrollbar max-h-[60px] md:max-h-[80px]" data-id="element-20328">
                          {dayEvents.map(event => <div key={event.id} className={`text-[10px] md:text-xs px-1.5 py-0.5 md:py-1 rounded truncate ${event.bg} ${event.color} font-medium`} title={event.title} data-id="element-20329">
                              {event.title}
                            </div>)}
                        </div>

                        {/* Tap/hover tooltip for days with events */}
                        {dayEvents.length > 0 && activeTooltipDay === i && <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 z-30 animate-in fade-in duration-150" onClick={e => e.stopPropagation()} data-id="element-20330">
                            <div className="bg-slate-900 text-white text-[11px] sm:text-xs rounded-xl shadow-xl px-3 py-2 min-w-[140px] max-w-[200px]" data-id="element-20331">
                              {dayEvents.map((event, idx) => <div key={event.id} className={`${idx > 0 ? 'mt-1.5 pt-1.5 border-t border-slate-700' : ''}`} data-id="element-20332">
                                  <div className="font-medium truncate" data-id="element-20333">
                                    {event.title}
                                  </div>
                                  <div className="text-slate-400 text-[10px] mt-0.5" data-id="element-20334">
                                    {event.type}
                                  </div>
                                </div>)}
                              <button onClick={e => {
                      e.stopPropagation();
                      setActiveTooltipDay(null);
                      onNavigate('calendar-event');
                    }} className="mt-2 pt-1.5 border-t border-slate-700 text-indigo-300 hover:text-indigo-200 font-medium text-[11px] flex items-center gap-1 w-full transition-colors" data-id="element-20335">
                                Подробнее <ArrowRight className="w-3 h-3" data-id="element-20336" />
                              </button>
                              <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-slate-900" data-id="element-20337" />
                            </div>
                          </div>}
                      </div>;
            })}
                </div>
              </Card>}
          </motion.div>}

        {/* ==================== SHARED CTA ==================== */}
        <div className="mt-12 sm:mt-16 bg-gradient-to-br from-slate-900 to-indigo-950 rounded-3xl p-6 sm:p-12 text-center relative overflow-hidden shadow-xl" data-id="element-20338">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-20339" />
          <div className="relative z-10" data-id="element-20340">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4" data-id="element-20341">
              Хотите персональный прогноз?
            </h2>
            <p className="text-sm sm:text-base text-indigo-200 mb-6 sm:mb-8 max-w-xl mx-auto" data-id="element-20342">
              Общий гороскоп — это только начало. Закажите индивидуальный
              прогноз транзитов, построенный по вашей натальной карте.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center" data-id="element-20343">
              <button onClick={() => onNavigate('service-forecast')} className="px-6 sm:px-8 py-3 bg-white text-slate-900 rounded-xl font-semibold hover:bg-slate-100 transition-colors text-sm sm:text-base" data-id="element-20344">
                Заказать прогноз транзитов
              </button>
              <button onClick={() => onNavigate('free-chart')} className="px-6 sm:px-8 py-3 border border-white/20 text-white rounded-xl font-semibold hover:bg-white/10 transition-colors text-sm sm:text-base" data-id="element-20345">
                Бесплатная карта
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}